/* eslint-disable react-hooks/exhaustive-deps */

import { useCallback, useEffect, useState } from 'react';
import { TableParams } from 'modules/types/TableParams';
import { TableColumn } from '../ui/table';
import { useHeaderSearchValue } from '../ui/headerSearch/useHeaderSearchValue';
import { SortingParams, SortType } from '../types/PagingParams';
import { Filter } from '../billsPage/redux/slice';

const initialTableParams: TableParams = {
  sort: [],
  search: '',
  currentPage: 1,
  currentRowCount: 10,
};

export const useTableParams = (
  fetchMoreItems: (
    page: number,
    sort?: SortingParams[],
    search?: string,
    currentRowCount?: number,
  ) => void,
  fetchMoreItemsWithFilters: (
    page: number,
    sort?: SortingParams[],
    search?: string,
    currentRowCount?: number,
    filter?: Filter[],
  ) => void,
  type?: any,
  setTableColumns?: (
    value: ((prevState: TableColumn[]) => TableColumn[]) | TableColumn[],
  ) => void,
  filters?: Filter[],
) => {
  const [tableParams, setTableParams] =
    useState<TableParams>(initialTableParams);
  const { itemId, resetLocationState } = useHeaderSearchValue();
  const [tabChange, setTabChange] = useState(false);

  useEffect(() => {
    setTableParams(initialTableParams);
    setTabChange(true);
  }, [type]);

  useEffect(() => {
    if (!itemId) {
      if (filters !== undefined && filters?.length > 0) {
        fetchMoreItemsWithFilters(
          tableParams.currentPage || 1,
          tableParams.sort,
          tableParams.search,
          tableParams.currentRowCount,
          filters,
        );
      } else {
        fetchMoreItems(
          tableParams.currentPage || 1,
          tableParams.sort,
          tableParams.search,
          tableParams.currentRowCount,
        );
      }
      setTabChange(false);
    }
  }, [
    tableParams.sort,
    tableParams.currentPage,
    tabChange,
    tableParams.currentRowCount,
  ]);

  const handleSearch = useCallback(
    (search?: string) => {
      setTableParams((prevState) => ({
        ...prevState,
        search: search ?? prevState.search,
        currentPage: 1,
      }));
      fetchMoreItems(1, tableParams.sort, search, tableParams.currentRowCount);
      setTabChange(false);
    },
    [tableParams.sort, type, filters],
  );

  const handleFilterChange = useCallback(
    (search?: string) => {
      setTableParams((prevState) => ({
        ...prevState,
        search: search ?? prevState.search,
        currentPage: 1,
      }));
      fetchMoreItemsWithFilters(
        1,
        tableParams.sort,
        search,
        tableParams.currentRowCount,
        filters,
      );
      setTabChange(false);
    },
    [filters],
  );

  const handlePageChange = (pageNumber: number) => {
    setTableParams((prevState) => ({ ...prevState, currentPage: pageNumber }));
  };

  const handleRowCountChange = (rowCount: number) => {
    setTableParams((prevState) => ({
      ...prevState,
      currentPage: 1,
      currentRowCount: rowCount,
    }));
  };

  const handleSearchChange = (value: string) => {
    setTableParams((prevState) => ({ ...prevState, search: value }));
  };

  const handleSortChange = useCallback(
    (column: string, newSort: SortType, newColumnsState?: TableColumn[]) => {
      const otherSortValues = tableParams.sort?.filter(
        (entry) => entry?.column !== column,
      );
      if (newSort === SortType.DEFAULT) {
        setTableParams((prevState) => ({
          ...prevState,
          sort: otherSortValues,
        }));
      } else {
        setTableParams((prevState) => ({
          ...prevState,
          sort: [...otherSortValues, { column, sort: newSort }],
        }));
      }
      if (setTableColumns) {
        newColumnsState && setTableColumns(newColumnsState);
      }
    },
    [tableParams.sort, setTableColumns],
  );

  useEffect(() => {
    if (itemId) {
      handleSearch(itemId.trimEnd());
      resetLocationState();
    }
  }, [itemId, handleSearch, resetLocationState]);

  return {
    tableParams,
    handleSearch,
    handlePageChange,
    handleSearchChange,
    handleSortChange,
    handleRowCountChange,
    handleFilterChange,
  };
};
